import { createFileRoute } from "@tanstack/react-router";
import { useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/lib/auth";
import { setRedirectIntent } from "@/lib/redirect-intent";
import { MrDriveAvatar } from "@/components/MrDriveAvatar";
import { INVITE_REF_KEY, sanitizePublicDescription as sanitizeEventDescription } from "@/routes/e.$eventId";

/** Как у мероприятий, плюс скрываем @-ники, чтобы контакт автора не был виден до входа. */
function sanitizePublicDescription(text: string | null | undefined): string {
  return sanitizeEventDescription(text).replace(/(^|[^\w])@[A-Za-z0-9_.]{2,}/g, "$1").replace(/[ \t]{2,}/g, " ");
}
import { EMPLOYMENT_LABEL, formatDeadline, formatPay, placeLabel, type WorkKind, type WorkSide } from "@/lib/work";

const SITE = "https://turkey-drive.com";
const OG_IMAGE = `${SITE}/assets/social-preview.jpg`;

type PublicWorkPost = {
  id: string;
  kind: WorkKind;
  side: WorkSide;
  title: string;
  description: string | null;
  category: string;
  city: string | null;
  is_online: boolean;
  employment: string | null;
  pay_amount: number | null;
  pay_currency: string;
  pay_period: string;
  deadline: string | null;
  author_name: string | null;
  author_photo_url: string | null;
  expires_at: string;
};

async function loadPublicWorkPost(postId: string): Promise<PublicWorkPost | null> {
  const { data, error } = await (supabase as any).rpc("public_work_post", { p_post_id: postId });
  if (error) return null;
  const rows = (Array.isArray(data) ? data : data ? [data] : []) as PublicWorkPost[];
  return rows[0] ?? null;
}

export const Route = createFileRoute("/w/$postId")({
  loader: ({ params }) => loadPublicWorkPost(params.postId),
  head: ({ params, loaderData }) => {
    const url = `${SITE}/w/${params.postId}`;
    if (!loaderData) {
      return {
        meta: [{ title: "Работа — TurkeyDrive" }, { name: "robots", content: "noindex" }],
        links: [{ rel: "canonical", href: url }],
      };
    }
    const title = `${loaderData.title} — TurkeyDrive`;
    const description =
      sanitizePublicDescription(loaderData.description).replace(/\s+/g, " ").slice(0, 155) ||
      `${loaderData.kind === "vacancy" ? "Вакансия" : "Заказ"} в сообществе TurkeyDrive · ${loaderData.category}`;
    return {
      meta: [
        { title },
        { name: "description", content: description },
        { property: "og:type", content: "article" },
        { property: "og:title", content: title },
        { property: "og:description", content: description },
        { property: "og:url", content: url },
        { property: "og:image", content: OG_IMAGE },
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:title", content: title },
        { name: "twitter:description", content: description },
        { name: "twitter:image", content: OG_IMAGE },
      ],
      links: [{ rel: "canonical", href: url }],
    };
  },
  component: PublicWorkPage,
  codeSplitGroupings: [["component", "errorComponent", "notFoundComponent"]],
  errorComponent: WorkUnavailable,
  notFoundComponent: WorkUnavailable,
});

function WorkUnavailable() {
  return (
    <div className="flex min-h-screen flex-col items-center justify-center gap-4 px-6 text-center">
      <MrDriveAvatar variant="home" size={96} />
      <h1 className="font-brand text-xl font-bold text-td-dark">Запись недоступна</h1>
      <p className="text-sm text-td-muted">Возможно, её уже закрыли или срок публикации истёк.</p>
      <a href="/work" className="rounded-full bg-td-teal px-5 py-2.5 text-sm font-semibold text-white">
        Смотреть другие вакансии
      </a>
    </div>
  );
}

function PublicWorkPage() {
  const post = Route.useLoaderData();
  const { postId } = Route.useParams();
  const { user } = useAuth();

  useEffect(() => {
    try {
      const ref = new URL(window.location.href).searchParams.get("ref");
      if (ref) window.localStorage.setItem(INVITE_REF_KEY, ref);
    } catch {
      /* ignore */
    }
  }, []);

  useEffect(() => {
    if (!user) return;
    window.location.replace(`/work?post=${postId}`);
  }, [user, postId]);

  if (!post) return <WorkUnavailable />;
  if (user) return null;

  const place = placeLabel(post) || "Онлайн";
  const deadline = post.kind === "order" ? formatDeadline(post.deadline) : null;
  const description = sanitizePublicDescription(post.description);

  function goToAuth(mode: "signup" | "signin") {
    const target = `/work?post=${postId}`;
    setRedirectIntent(target);
    window.location.assign(`/auth?redirect=${encodeURIComponent(target)}&mode=${mode}`);
  }

  return (
    <main className="min-h-screen overflow-x-hidden bg-gradient-to-b from-td-teal/10 via-white to-white pb-36">
      <div className="mx-auto w-full min-w-0 max-w-[520px] px-4 pt-5">
        <a href="/" className="text-[13px] text-td-muted hover:text-td-dark">← TurkeyDrive</a>

        <div className="mt-5 flex flex-wrap items-center gap-1.5">
          <span className="rounded-full bg-td-teal/10 px-2.5 py-0.5 text-xs font-medium text-td-teal">
            {post.kind === "vacancy" ? "Вакансия" : "Заказ"}
          </span>
        </div>

        <h1 className="mt-3 break-words font-brand text-2xl font-bold leading-tight text-td-dark [overflow-wrap:anywhere]">
          {post.title}
        </h1>

        <div className="mt-3 space-y-1.5 text-sm text-td-dark">
          <div>🗂 {post.category}</div>
          <div>📍 {place}</div>
          {post.kind === "vacancy" && post.employment && <div>🕒 {EMPLOYMENT_LABEL[post.employment] ?? post.employment}</div>}
          <div>💰 {formatPay(post)}</div>
          {deadline && <div>⏳ Срок: {deadline}</div>}
        </div>

        {description && (
          <p className="mt-5 whitespace-pre-line break-words text-[15px] leading-relaxed text-td-dark [overflow-wrap:anywhere]">
            {description}
          </p>
        )}

        {post.author_name && (
          <div className="mt-6 flex min-w-0 items-center gap-3 rounded-2xl border border-td-line bg-white p-3">
            {post.author_photo_url ? (
              <img decoding="async" src={post.author_photo_url} alt={post.author_name} className="h-11 w-11 rounded-full object-cover" loading="lazy" />
            ) : (
              <div className="flex h-11 w-11 items-center justify-center rounded-full bg-td-soft text-td-muted">🙂</div>
            )}
            <div className="min-w-0 flex-1">
              <div className="text-xs text-td-muted">Разместил(а)</div>
              <div className="break-words text-sm font-semibold text-td-dark [overflow-wrap:anywhere]">{post.author_name}</div>
            </div>
          </div>
        )}

        <p className="mt-6 text-center text-[13px] text-td-muted">Контакты и отклик доступны резидентам TurkeyDrive</p>
      </div>

      <div className="fixed inset-x-0 bottom-0 z-50 border-t border-td-line bg-white/95 p-4 pb-[calc(env(safe-area-inset-bottom)+16px)] backdrop-blur">
        <div className="mx-auto max-w-[520px]">
          <button type="button" onClick={() => goToAuth("signup")} className="cta-solid w-full rounded-full px-6 py-3.5 text-base">
            Стать резидентом и откликнуться
          </button>
          <button
            type="button"
            onClick={() => goToAuth("signin")}
            className="mt-2 w-full text-center text-[12px] font-medium text-td-teal underline underline-offset-2"
          >
            Уже есть аккаунт? Войти
          </button>
        </div>
      </div>
    </main>
  );
}
